import {useApp} from "../app.context";
import {useIntegrator} from "../integrator.context";
import {prepareImageUpload} from "./file.utils";
import {classifyImageKeys} from "./imageClassification";
import {offerOrphanImageCleanup} from "./orphanImageCleanup";
import {SetImagePackSlotCommand} from "../commands/setImagePackSlot.command";
import {AddImageToPackCommand} from "../commands/addImageToPack.command";
import {SetMetadataIconCommand} from "../commands/setMetadataIcon.command";
import {SetBuildingConstructionImageCommand} from "../commands/setBuildingConstructionImage.command";

// What an uploaded/assigned image is FOR — determines where the choice is
// written and how a fresh upload is keyed.
//   packSlot:     an image pack slot (entity visuals); writes the slot,
//                 uploads under the slot-derived key.
//   metadataIcon: a metadata instance's icon (attributes / rewards / tech);
//                 uploads go directly under the uid.
export type UploadTarget =
    | { kind: 'packSlot'; packId: string; level: number; variation: number }
    | { kind: 'metadataIcon'; uid: string }
    | { kind: 'buildingConstruction'; buildingId: string; index: number }
    // A bare RemoteConstants image key (e.g. an order's "ui.icon.MoveOrder").
    // No indirection: the client reads images[key] directly, so upload/pick
    // write straight to that key — there's no slot/pack/metadata to wire.
    | { kind: 'imageKey'; key: string };

// Mint a construction-image key for a building that doesn't collide with an
// existing image. Construction images are a flat ordered list (not positional
// like pack slots), so the name is just a unique id — its array position, not
// its name, determines which construction turn it covers.
export function freshConstructionKey(images: Record<string, unknown>, buildingId: string): string {
    let n = 0;
    let key = `${buildingId}_construction_${n}`;
    while (key in images) { n++; key = `${buildingId}_construction_${n}`; }
    return key;
}

// Mint a pack-slot image key for a NEW slot that doesn't collide with an
// existing image. Positional keys (`${packId}_${level}_${variation}`) get
// reused after add/remove churn, so a "new" slot can land on a key another
// slot already owns — uploading/assigning there would silently hijack that
// other slot. Bump the variation index until the key is free.
export function freshSlotKey(images: Record<string, unknown>, packId: string, level: number, variation: number): string {
    let v = variation;
    let key = `${packId}_${level}_${v}`;
    while (key in images) { v++; key = `${packId}_${level}_${v}`; }
    return key;
}

// Shared "upload a file onto a target" operation: the heart of the image
// picker's upload path, lifted out so both the picker and the asset-pack
// popup drive one implementation. Handles the shared-image confirm guard, the
// scale+upload (which archives the displaced file when replacing in place),
// and wiring a fresh upload into an empty pack slot or clearing a metadata
// indirection. Returns true on success, false if cancelled or the upload fails.
export function useImageUpload() {
    const {state, executeCommand, goBack} = useIntegrator();
    const {uploadFile, getLocalizationValue, confirm, choose, setNotification, attributeManifest} = useApp();

    async function applyUpload(
        target: UploadTarget,
        currentKey: string | undefined,
        file: File,
        onLoadingChange?: (loading: boolean) => void,
    ): Promise<boolean> {
        // Upload key:
        // - OCCUPIED pack slot: the slot's CURRENT key — replace-in-place, so
        //   "upload a new file here" updates the asset everywhere it's shown
        //   instead of minting a parallel positional asset. Old file goes to
        //   the archive (handled inside uploadFile).
        // - empty/placeholder slot: a fresh positional key (new asset).
        // - metadata icon: the uid (replace-in-place by construction).
        let uploadKey: string;
        if (target.kind === 'packSlot') {
            uploadKey = currentKey ?? freshSlotKey(state.images ?? {}, target.packId, target.level, target.variation);
        } else if (target.kind === 'buildingConstruction') {
            // Occupied slot: replace in place (old file → archive). Empty: mint a new key.
            uploadKey = currentKey ?? freshConstructionKey(state.images ?? {}, target.buildingId);
        } else if (target.kind === 'metadataIcon') {
            uploadKey = target.uid;
        } else {
            // imageKey — the key IS the image entry (replace-in-place, no wiring).
            uploadKey = target.key;
        }

        // Replacing an image that several things share changes all of them —
        // ask first (same guard as the Images tab's Replace button).
        const classification = classifyImageKeys(state, getLocalizationValue, attributeManifest);
        const usages = classification.get(uploadKey)?.usedBy ?? [];
        if (usages.length > 1) {
            const proceed = await confirm({
                title: 'Replace shared image?',
                body: (
                    <div className={'layout vertical gap'}>
                        <p>The image at this key is used in <strong>{usages.length}</strong> places —
                            uploading over it changes all of them:</p>
                        {usages.map((u, i) => <p key={i} className={'muted-text'}>• {u.label}</p>)}
                    </div>
                ),
                confirmText: 'Replace everywhere',
                danger: true,
            });
            if (!proceed) return false;
        }

        // Source assets go up as authored — prepareImageUpload only converts a
        // non-PNG to PNG (same pixels), it never resizes.
        let upload: File;
        try {
            upload = await prepareImageUpload(file);
        } catch (e) {
            setNotification({
                title: 'Could not read that image',
                message: e instanceof Error ? e.message : String(e),
            });
            return false;
        }

        onLoadingChange?.(true);
        const ok = await uploadFile(uploadKey, upload);
        onLoadingChange?.(false);
        if (!ok) return false;

        if (target.kind === 'packSlot' && !currentKey) {
            // Empty/placeholder slot: register the new positional asset
            // in the pack. (Occupied slots were replaced in place — the
            // slot already points at uploadKey, nothing to rewire.)
            executeCommand(new AddImageToPackCommand(target.packId, target.level, target.variation));
            executeCommand(new SetImagePackSlotCommand(target.packId, target.level, target.variation, uploadKey));
        } else if (target.kind === 'buildingConstruction' && !currentKey) {
            // Empty slot: append the new key to the building's construction
            // list. (Occupied slots were replaced in place — same key.)
            executeCommand(new SetBuildingConstructionImageCommand(target.buildingId, target.index, uploadKey));
        } else if (target.kind === 'metadataIcon') {
            // The fresh upload lives directly under the uid — clear any
            // pick-existing indirection so it actually displays.
            executeCommand(new SetMetadataIconCommand(target.uid, undefined));
        }
        // Only the metadata-icon path can still displace an image (a
        // previously-borrowed shared key loses this reference when the
        // indirection clears) — offer archive/delete/keep.
        if (currentKey && currentKey !== uploadKey) {
            await offerOrphanImageCleanup(currentKey,
                {state, getLocalizationValue, attributeManifest, choose, executeCommand, goBack}, 'keep');
        }
        return true;
    }

    return {applyUpload};
}
